// A2 Project dashboard (round 13, 04/Oct/26):
//   left  — "Statistics" card: Issues Tracker numbers + open by discipline (two columns);
//           "Activity statistics" card: running-total graph with date range (core/ui/ActivityStats.tsx);
//           Progress reporting placeholder. Both lower cards collapse when you click their title.
//   right — "Recent activity": the latest entries only (no filter) + View all activity → A2.2.
import { useRpc } from "../api/useRpc";
import { href } from "../router";
import { useSession } from "../session";
import { CodeChip, ErrorBox, InfoIcon, Loading, PageHeader, useCollapsed } from "../ui/kit";
import { ActivityLine } from "../ui/activity";
import { ActivityStats } from "../ui/ActivityStats";
import { IssuesKpis, OpenByDiscipline } from "../../modules/issues/components/DashboardStats";
import { Icon } from "../ui/icons";

export function DashboardPage({ code }: { code: string }) {
  const { me } = useSession();
  const { data, error } = useRpc("project_dashboard", { code });
  const [progressCollapsed, toggleProgress] = useCollapsed("progress");
  if (error) return <ErrorBox message={error} />;
  if (!data) return <Loading />;
  const progress = data.modules.find((m) => m.id === "progress");
  return (
    <>
      <PageHeader crumb={<>{data.project.name} <CodeChip code={data.project.code} /> › Dashboard</>}
        title={<>{data.project.name} <span className="title-sub">{data.project.client} · {data.members.length} members</span></>} />
      <div className="dash">
        <div className="dash-main">
          <section className="card pad stats-card" data-testid="statistics">
            <h2>Statistics</h2>
            {data.issues && <IssuesKpis stats={data.issues} />}
            {data.issues && <OpenByDiscipline stats={data.issues} disciplines={data.disciplines} />}
          </section>
          <ActivityStats code={code} today={me.today} progressOn={progress?.enabled === true} />
          {progress && !progress.enabled && (
            <section className={`tile placeholder${progressCollapsed ? " collapsed" : ""}`} data-testid="progress-card">
              <h2 className="row gap6">
                <button type="button" className="collapse-head" aria-expanded={!progressCollapsed} onClick={toggleProgress}>
                  <span className="chev"><Icon name={progressCollapsed ? "chevron_right" : "expand_more"} /></span>Progress reporting</button>
                <InfoIcon tip={["Planned vs actual progress per component and a weekly progress report.", "Shown here once the module is switched on.", "Click the card title to collapse or expand it (▸ collapsed, ▾ open)."]} />
                <span className="soon">placeholder module</span></h2>
              {!progressCollapsed && <div className="ghost-bars">{[30, 45, 60, 72, 85, 95].map((h, k) => <div key={k}><i style={{ height: `${h}%` }} /><i style={{ height: `${h * 0.82}%` }} /></div>)}</div>}
            </section>
          )}
        </div>
        <section className="card pad activity" data-testid="recent-activity">
          <h2>Recent activity</h2>
          <div className="act-list">
            {data.activity.length === 0 && <p className="muted">Nothing yet.</p>}
            {data.activity.map((a) => <ActivityLine key={a.id} a={a} code={code} />)}
          </div>
          <a className="link small" href={href({ name: "activity", code })}>View all activity →</a>
        </section>
      </div>
    </>
  );
}
